/**
 * Security settings — currently scoped to per-user MFA management.
 *
 * Flow:
 *   1. If MFA enabled: show status + Disable button + backup-codes counter.
 *   2. If not enabled: show Enroll button → opens wizard
 *      a) Click "Enroll" → mfaApi.enrollInit() → modal with QR + secret
 *      b) User scans / types secret into authenticator app
 *      c) User types first 6-digit code → mfaApi.enrollVerify(code)
 *      d) On success: modal switches to "Save these backup codes" screen
 *      e) User confirms → close → status refreshed
 *
 * Backup codes are shown EXACTLY ONCE here. They are persisted as argon2
 * hashes server-side; if the user closes the modal without saving them,
 * they're gone forever (recovery = admin reset → re-enroll).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Divider,
  Group,
  Image,
  Loader,
  Modal,
  PasswordInput,
  PinInput,
  Stack,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconCopy,
  IconKey,
  IconLock,
  IconLockOpen,
  IconRefresh,
  IconShieldCheck,
} from '@tabler/icons-react';
import { mfaApi, type MfaEnrollInit } from '../api/mfa';
import { describeError } from '../api/client';
import { formatGmt7 } from '../lib/format';
import { useTranslation } from '../i18n/LocaleContext';

export function SecurityPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const statusQuery = useQuery({
    queryKey: ['mfa-status'],
    queryFn: () => mfaApi.status(),
  });

  const [enrollOpen, setEnrollOpen] = useState(false);
  const [disableOpen, setDisableOpen] = useState(false);

  const invalidateStatus = () =>
    queryClient.invalidateQueries({ queryKey: ['mfa-status'] });

  return (
    <Stack gap="md">
      <div>
        <Title order={2}>{t('security.title')}</Title>
        <Text c="dimmed" size="sm">
          {t('security.subtitle')}
        </Text>
      </div>

      <Card>
        {statusQuery.isLoading && (
          <Group justify="center" py="xl">
            <Loader />
          </Group>
        )}
        {statusQuery.error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {describeError(statusQuery.error, t('security.statusLoadFailed'))}
          </Alert>
        )}
        {statusQuery.data && (
          <Stack gap="md">
            <Group justify="space-between" align="flex-start">
              <Group gap="xs">
                <IconShieldCheck size={28} color={statusQuery.data.enabled ? 'teal' : 'gray'} />
                <div>
                  <Group gap="xs">
                    <Text fw={600}>{t('security.mfaTitle')}</Text>
                    {statusQuery.data.enabled ? (
                      <Badge color="green" variant="filled">
                        {t('security.badgeActive')}
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light">
                        {t('security.badgeNotEnabled')}
                      </Badge>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed" mt={2}>
                    {t('security.mfaHint')}
                  </Text>
                </div>
              </Group>
              <Group gap="xs">
                {statusQuery.data.enabled ? (
                  <Button
                    color="red"
                    variant="light"
                    leftSection={<IconLockOpen size={14} />}
                    onClick={() => setDisableOpen(true)}
                  >
                    {t('security.disableMfa')}
                  </Button>
                ) : (
                  <Button leftSection={<IconLock size={14} />} onClick={() => setEnrollOpen(true)}>
                    {t('security.enrollAuthenticator')}
                  </Button>
                )}
              </Group>
            </Group>

            {statusQuery.data.enabled && (
              <>
                <Divider />
                <Group gap="xl">
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('security.labelEnrolled')}</Text>
                    <Text size="sm">{formatGmt7(statusQuery.data.enrolledAt)}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('security.labelLastUsed')}</Text>
                    <Text size="sm">{formatGmt7(statusQuery.data.lastUsedAt)}</Text>
                  </div>
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('security.labelBackupCodesLeft')}</Text>
                    <Text
                      size="sm"
                      fw={500}
                      c={statusQuery.data.backupCodesRemaining < 3 ? 'red' : undefined}
                    >
                      {statusQuery.data.backupCodesRemaining} / 10
                    </Text>
                  </div>
                </Group>
                {statusQuery.data.backupCodesRemaining < 3 && (
                  <Alert color="yellow" icon={<IconAlertCircle size={14} />} variant="light">
                    {t('security.backupCodesLowWarning')}
                  </Alert>
                )}
              </>
            )}
          </Stack>
        )}
      </Card>

      <EnrollWizard
        opened={enrollOpen}
        onClose={() => {
          setEnrollOpen(false);
          invalidateStatus();
        }}
      />
      <DisableModal
        opened={disableOpen}
        onClose={() => {
          setDisableOpen(false);
          invalidateStatus();
        }}
      />
    </Stack>
  );
}

// ===========================================================================
// Enroll wizard: 2 steps — show QR, then verify code → show backup codes
// ===========================================================================

function EnrollWizard({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [step, setStep] = useState<'init' | 'verify' | 'codes'>('init');
  const [initData, setInitData] = useState<MfaEnrollInit | null>(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const initMut = useMutation({
    // returns the QR/secret to display; not a save. Errors are shown inline
    // in the wizard (with a retry button), so suppress the global toast too.
    meta: { successNotification: false, errorNotification: false },
    mutationFn: () => mfaApi.enrollInit(),
    onSuccess: (data) => {
      setInitData(data);
      setStep('verify');
      setErrorMessage(null);
    },
    onError: (err) => setErrorMessage(describeError(err, t('security.enrollErrInit'))),
  });

  const verifyMut = useMutation({
    meta: {
      successKey: 'notify.mfa.enable.ok',
      errorKey: 'notify.mfa.enable.fail',
    },
    mutationFn: () => mfaApi.enrollVerify(code.trim()),
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes);
      setStep('codes');
      setErrorMessage(null);
    },
  });

  const reset = () => {
    setStep('init');
    setInitData(null);
    setCode('');
    setBackupCodes([]);
    setErrorMessage(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  // Auto-kick the init mutation when the modal opens fresh.
  useMemo(() => {
    if (opened && step === 'init' && !initData && !initMut.isPending) {
      initMut.mutate();
    }
  }, [opened, step, initData, initMut]);

  return (
    <Modal
      opened={opened}
      onClose={() => {
        // Disallow closing during the backup-codes step until user explicitly
        // confirms — backup codes are shown ONCE.
        if (step === 'codes') return;
        close();
      }}
      title={
        step === 'codes' ? t('security.modalTitleSaveCodes') : t('security.modalTitleEnroll')
      }
      size="lg"
      closeOnClickOutside={step !== 'codes'}
      closeOnEscape={step !== 'codes'}
      withCloseButton={step !== 'codes'}
    >
      {step === 'init' && (
        <Group justify="center" py="xl">
          <Loader />
          <Text size="sm">{t('security.generatingSecret')}</Text>
        </Group>
      )}

      {step === 'verify' && initData && (
        <Stack gap="md">
          <Text
            size="sm"
            dangerouslySetInnerHTML={{ __html: t('security.enrollStep1') }}
          />
          <Group justify="center">
            <Image src={initData.qrDataUrl} alt={t('security.qrAlt')} w={240} h={240} />
          </Group>
          <Group gap="xs" justify="center">
            <Text size="xs" c="dimmed">{t('security.secretManual')}</Text>
            <Code style={{ fontSize: 12 }}>{initData.secret}</Code>
            <CopyButton value={initData.secret} timeout={1500}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? t('apiCred.copied') : t('apiCred.copy')}>
                  <ActionIcon size="xs" variant="default" onClick={copy}>
                    {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
          <Divider />
          <Text size="sm">
            {t('security.enrollStep2')}
          </Text>
          <Group justify="center">
            <PinInput length={6} type="number" value={code} onChange={setCode} oneTimeCode />
          </Group>
          <Group justify="flex-end">
            <Button variant="default" onClick={close} disabled={verifyMut.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => verifyMut.mutate()}
              loading={verifyMut.isPending}
              disabled={code.length !== 6}
              leftSection={<IconCheck size={14} />}
            >
              {t('security.verifyActivate')}
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'codes' && (
        <Stack gap="md">
          <Alert color="yellow" icon={<IconAlertCircle size={16} />} variant="light">
            <Text fw={600} size="sm">{t('security.saveCodesTitle')}</Text>
            <Text size="xs" mt={4}>
              {t('security.saveCodesBody')}
            </Text>
          </Alert>
          <Card withBorder>
            <Stack gap={4}>
              {backupCodes.map((c, i) => (
                <Group key={c} gap="xs">
                  <Text size="xs" c="dimmed" w={24} ta="right">
                    {i + 1}.
                  </Text>
                  <Code style={{ fontSize: 13 }}>{c}</Code>
                </Group>
              ))}
            </Stack>
          </Card>
          <Group gap="xs" justify="center">
            <CopyButton value={backupCodes.join('\n')} timeout={2000}>
              {({ copied, copy }) => (
                <Button
                  onClick={copy}
                  leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  variant="default"
                >
                  {copied ? t('security.copiedAll10') : t('security.copyAll')}
                </Button>
              )}
            </CopyButton>
          </Group>
          <Group justify="flex-end">
            <Button onClick={close} leftSection={<IconShieldCheck size={14} />}>
              {t('security.savedClose')}
            </Button>
          </Group>
        </Stack>
      )}

      {step === 'init' && errorMessage && (
        <Alert color="red" icon={<IconAlertCircle size={14} />} variant="light" mt="md">
          {errorMessage}
          <Group mt="xs">
            <Button size="xs" onClick={() => initMut.mutate()} leftSection={<IconRefresh size={12} />}>
              {t('security.retry')}
            </Button>
          </Group>
        </Alert>
      )}
    </Modal>
  );
}

// ===========================================================================
// Disable modal — requires password + current code
// ===========================================================================

function DisableModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  const disableMut = useMutation({
    meta: {
      successKey: 'notify.mfa.disable.ok',
      errorKey: 'notify.mfa.disable.fail',
    },
    mutationFn: () => mfaApi.disable(password, code.trim()),
    onSuccess: () => {
      setPassword('');
      setCode('');
      onClose();
    },
  });

  return (
    <Modal
      opened={opened}
      onClose={() => {
        if (disableMut.isPending) return;
        setPassword('');
        setCode('');
        onClose();
      }}
      title={t('security.disableMfaTitle')}
      size="md"
    >
      <Stack gap="md">
        <Alert color="red" icon={<IconAlertCircle size={16} />} variant="light">
          <Text size="sm">
            {t('security.disableWarningPre')} <strong>{t('security.disableWarningBold')}</strong>{' '}
            {t('security.disableWarningPost')}
          </Text>
        </Alert>
        <PasswordInput
          label={t('security.accountPasswordLabel')}
          value={password}
          onChange={(e) => setPassword(e.currentTarget.value)}
          required
          leftSection={<IconKey size={14} />}
        />
        <div>
          <Text size="sm" fw={500} mb={4}>{t('security.mfaCodeLabel')}</Text>
          <PinInput length={6} type="number" value={code} onChange={setCode} oneTimeCode />
        </div>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={disableMut.isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            color="red"
            onClick={() => disableMut.mutate()}
            loading={disableMut.isPending}
            disabled={!password || code.length !== 6}
          >
            {t('security.disableMfa')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// Suppress unused-warning for the TextInput import (kept in case future fields).
void TextInput;
